import React, {useEffect, useState} from 'react';
import Modal from 'react-modal';
import axios from 'axios';
import {
    XCircleIcon,
    BuildingOffice2Icon,
    CalendarDaysIcon,
    CurrencyDollarIcon,
    UserIcon,
    DocumentTextIcon,
    UsersIcon,
    ChevronDownIcon,
    ChevronUpIcon
} from "@heroicons/react/24/outline";
import {Spin} from "antd";
import {useHistory} from "react-router-dom"; // v5 기준
import "../../css/global.css";
import {MDBIcon, MDBTooltip} from "mdb-react-ui-kit";

const CATEGORY_FIELDS = {
    '공고일반': [
        ['공고종류', 'ntceKindNm'],
        ['입찰공고번호', 'bidNtceNo'],
        ['공고명', 'bidNtceNm'],
        ['입찰방식', 'bidMethdNm'],
        ['낙찰하한율', 'sucsfbidLwltRate'],
        ['계약방법', 'cntrctCnclsMthdNm'],
        ['게시일시', 'bidNtceDt'],
        ['참조번호', 'refNo'],
    ],
    '입찰일정': [
        ['입찰개시일', 'bidBeginDt'],
        ['입찰마감일', 'bidClseDt'],
        ['개찰일시', 'opengDt'],
        ['재입찰 개찰일시', 'rbidOpengDt'],
    ],
    '예산 및 가격': [
        ['배정예산액', 'asignBdgtAmt'],
        ['추정가격', 'presmptPrce'],
        ['부가가치세', 'VAT'],
    ],
    '기관 및 담당자': [
        ['공고기관', 'ntceInsttNm'],
        ['수요기관', 'dminsttNm'],
        ['계약의뢰기관', 'crdtrNm'],
        ['담당자명', 'ntceInsttOfclNm'],
        ['연락처', 'ntceInsttOfclTelNo'],
        ['이메일', 'ntceInsttOfclEmailAdrs'],
    ],
};

const formatValue = (key, value) => {
    if (!value) return '-';
    if (['asignBdgtAmt', 'presmptPrce', 'VAT'].includes(key)) {
        return `₩ ${new Intl.NumberFormat().format(Number(value))}`;
    }
    return value;
};

const CategoryTable = ({title, fields, data}) => {
    const ICONS = {
        '공고일반': <BuildingOffice2Icon className="w-5 h-5 text-blue-600"/>,
        '입찰일정': <CalendarDaysIcon className="w-5 h-5 text-indigo-600"/>,
        '예산 및 가격': <CurrencyDollarIcon className="w-5 h-5 text-green-600"/>,
        '기관 및 담당자': <UserIcon className="w-5 h-5 text-pink-600"/>,
    };
    return (
        <div className="mb-3 bg-white rounded-xl shadow-sm border w-full">
            <div className="bg-gray-50 px-4 py-2 text-gray-800 font-semibold border-b flex items-center gap-2">
                {ICONS[title]} {title}
            </div>
            <table className="w-full text-sm text-gray-700">
                <tbody>
                {fields.map(([label, key]) => (
                    <tr key={key} className="border-b last:border-none">
                        <td className="px-4 py-3 font-medium w-1/4 bg-gray-50">{label}</td>
                        <td className="px-4 py-3">
                            {label === '공고명' && data.bidNtceDtlUrl ? (
                                <div className="flex items-center justify-between">
                                    <span>{formatValue(key, data[key])}</span>
                                    <a
                                        href={data.bidNtceDtlUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 text-sm inline-flex items-center gap-1 ml-2 hover:underline"
                                    >
                                        원문 보기
                                        <ChevronUpIcon className="w-4 h-4"/>
                                    </a>
                                </div>
                            ) : (
                                formatValue(key, data[key])
                            )}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

// 🎈 유사공고 카드 패널
const SimilarBidCardList = ({data, loading, onClickBid}) => (

    <Spin spinning={loading} tip="유사 공고 목록 및 낙찰 업체 로딩 중..." size="large">
        <div
            className="w-[400px] min-w-[350px] overflow-y-auto bg-white rounded-xl shadow border p-4 flex flex-col gap-3">
            <div className="font-bold text-lg mb-1 px-1">유사공고 목록</div>
            <div className="text-sm mb-1 px-1">전체 결과가 나오지 않을 경우, 재시도 해주세요</div>
            <ul className="flex flex-col gap-3">
                {data.map(item => (
                    <li
                        key={item.bizNo + item.bizOrd}
                        className="border border-blue-100 rounded-2xl px-4 py-3 shadow group cursor-pointer hover:scale-[1.025] hover:border-blue-400 transition-all"
                        title={item.bizNm}
                        onClick={() => onClickBid && onClickBid(item)}
                    >

                        <div className="font-semibold text-blue-800 truncate group-hover:underline mb-1 text-base">
                            {item.bizNm.length > 32 ? item.bizNm.slice(0, 32) + '…' : item.bizNm}
                        </div>
                        <div className="flex flex-wrap text-xs text-gray-500 gap-x-3 mb-1">
                            <span>공고번호: {item.bizNo}</span>
                            <span>/ 차수: {item.bizOrd}</span>
                        </div>
                        {item.bidResultWinnerCompanyInfo && (

                            <div className="flex flex-col gap-0.5 text-xs">

                                {
                                    item.bidResultWinnerCompanyInfo.crawling &&
                                    <>
                                        <span>
                                            <span className="font-semibold text-red-500">{'<'} 개찰 결과 1순위 기업 {' '}
                                                <MDBTooltip tag="a" wrapperProps={{href: "#"}} title="나라장터 개찰 결과 1순위 기업 입니다 (일부 건은 금액 비공개)">
                                                 <MDBIcon far icon="question-circle"/>
                                            </MDBTooltip>
                                                {'>'}
                                            </span>


                                        </span>
                                    </>
                                }

                                <span>
                                    <span className="font-semibold text-gray-700">낙찰사</span>{' '}
                                    <span
                                        className="font-bold text-red-500">{item.bidResultWinnerCompanyInfo.winnerCompanyName}</span>
                                </span>
                                <span>
                                    <span className="font-semibold text-gray-700">금액</span>{' '}
                                    <span
                                        className="text-green-700">₩{item.bidResultWinnerCompanyInfo.bidAmount?.toLocaleString() || '-'}</span>
                                </span>
                                <span>
                                    <span className="font-semibold text-gray-700">일자</span>{' '}
                                    <span
                                        className="text-gray-700">{item.bidResultWinnerCompanyInfo.fnlSucsfDate}</span>
                                </span>

                            </div>
                        )}
                    </li>
                ))}
            </ul>

        </div>

    </Spin>


);

const SelectMoveModal = ({isOpen, onClose, bid, onBid, onCompany}) => (
    <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        className="max-w-xs w-full mx-auto mt-32 p-6 bg-white rounded-xl shadow-2xl outline-none"
        overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center z-50"
        ariaHideApp={false}
    >
        <div className="flex flex-col gap-2 items-center">
            <button
                className="w-full hover:bg-blue-100 font-semibold py-2 rounded-lg"
                onClick={onBid}
            >
                해당 공고 상세로 이동
            </button>
            <button
                className="w-full hover:bg-green-100 font-semibold py-2"
                onClick={onCompany}
                disabled={!bid?.bidResultWinnerCompanyInfo?.winnerCompanyBizNumber}
            >
                낙찰 사업자 상세로 이동
            </button>
            <button
                className="mt-3 text-gray-400 hover:text-gray-600"
                onClick={onClose}
            >
                취소
            </button>
        </div>
    </Modal>
);

const BidDetailModal = ({isOpen, onClose, bidNumber, bidNoticeOrder}) => {
    const history = useHistory();
    const [data, setData] = useState(null);
    const [similarBid, setSimilarBid] = useState([]);
    const [loading, setLoading] = useState(false);
    const [similarBidLoading, setSimilarLoading] = useState(false);
    const [showLosers, setShowLosers] = useState(false);
    const [selectedSimilar, setSelectedSimilar] = useState(null);
    const [selectModal, setSelectModal] = useState({open: false, bid: null});

    useEffect(() => {
        if (isOpen && bidNumber && bidNoticeOrder) {
            setLoading(true);
            setSimilarLoading(true);
            setData(null);
            setSimilarBid([]);
            axios
                .get(`${process.env.REACT_APP_API_BASE_URL}/bids/detail?bidNumber=${bidNumber}`)
                .then((res) => {
                    setData(res.data);
                    setLoading(false);
                })
                .catch(() => setLoading(false));
            axios
                .get(`${process.env.REACT_APP_API_BASE_URL}/bids/similar_bid?bidNumber=${bidNumber}&bidNoticeOrder=${bidNoticeOrder}`)
                .then((res) => {
                    setSimilarBid(res.data || []);
                    setSimilarLoading(false);
                })
                .catch(() => setSimilarLoading(false));
        }
    }, [bidNumber, bidNoticeOrder, isOpen]);

    const bidInfo = data || {};
    const biddingResults = data?.biddingResults || [];
    const sortedResults = [...biddingResults].sort((a, b) => (a.rank || Infinity) - (b.rank || Infinity));
    const winningBizNumber = biddingResults.find(
        r => r.winningBizNumber !== null && r.winningBizNumber !== undefined
    )?.winningBizNumber;

    // 유사공고 카드 클릭: 선택 모달 열기
    const handleSimilarBidClick = (item) => {
        setSelectModal({open: true, bid: item});
    };

    // 선택 모달: 공고 상세로
    const handleGoBidDetail = () => {
        setSelectedSimilar({
            bizNo: selectModal.bid.bizNo,
            bizOrd: selectModal.bid.bizOrd
        });
        setSelectModal({open: false, bid: null});
    };

    // 선택 모달: 사업자 상세로
// 선택 모달: 사업자 상세로 (새 탭에서 열기)
    const handleGoCompany = () => {
        const bizNumber = selectModal.bid?.bidResultWinnerCompanyInfo?.winnerCompanyBizNumber;
        if (bizNumber) {
            const url = `/search/companies/?bizNumber=${encodeURIComponent(bizNumber)}`;
            window.open(url, "_blank", "noopener,noreferrer");
        }
        setSelectModal({open: false, bid: null});
    };

    // 모달 닫기 및 선택된 유사공고 초기화
    const handleClose = () => {
        setSelectedSimilar(null);
        setSelectModal({open: false, bid: null});
        onClose();
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onRequestClose={handleClose}
                className="slow-animation max-w-[1400px] w-full mx-auto mt-16 p-0 bg-transparent rounded-xl shadow-2xl overflow-y-auto max-h-[90vh] outline-none"
                overlayClassName="fixed inset-0 bg-black/30 flex justify-center items-start z-50"
                ariaHideApp={false}
            >
                <div className="flex flex-row gap-8 p-6 bg-gray-100 rounded-xl">
                    {/* 좌측 본문 */}
                    <div className="flex-1 flex flex-col gap-3 min-w-0">
                        <div className="flex justify-between items-center mb-2">
                            <h2 className="text-xl font-bold text-gray-800">입찰 상세정보</h2>
                            <button onClick={handleClose}>
                                <XCircleIcon className="w-6 h-6 text-gray-700 hover:text-gray-600"/>
                            </button>
                        </div>
                        <Spin spinning={loading} tip="입찰 상세 정보 로딩 중..." size="large">
                            {/* 전체 카테고리 섹션 */}
                            <div className="flex flex-col md:flex-row md:flex-wrap gap-3">
                                {Object.entries(CATEGORY_FIELDS).map(([title, fields]) => (
                                    <CategoryTable key={title} title={title} fields={fields} data={bidInfo}/>
                                ))}
                            </div>
                            {/* 첨부파일 */}
                            <div className="bg-white rounded-xl shadow-sm border">
                                <div
                                    className="bg-gray-50 px-4 py-2 text-gray-800 font-semibold border-b flex items-center gap-2">
                                    <DocumentTextIcon className="w-5 h-5 text-sky-600"/>
                                    첨부파일
                                </div>
                                <ul className="p-4 space-y-2 text-sm">
                                    {Array.from({length: 10}).map((_, i) => {
                                        const url = bidInfo[`ntceSpecDocUrl${i + 1}`];
                                        const name = bidInfo[`ntceSpecFileNm${i + 1}`];
                                        if (!url) return null;
                                        return (
                                            <li key={i}>
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:underline flex items-center gap-2"
                                                >
                                                    <DocumentTextIcon className="w-4 h-4"/>
                                                    {name || `첨부파일 ${i + 1}`}
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </Spin>
                    </div>
                    {/* 우측: 유사공고 카드 패널 */}
                    <SimilarBidCardList
                        data={similarBid}
                        loading={similarBidLoading}
                        onClickBid={handleSimilarBidClick}
                    />
                </div>
            </Modal>
            {/* 유사공고 클릭 시 이동 선택 모달 */}
            <SelectMoveModal
                isOpen={selectModal.open}
                onClose={() => setSelectModal({open: false, bid: null})}
                bid={selectModal.bid}
                onBid={handleGoBidDetail}
                onCompany={handleGoCompany}
            />
            {/* 유사공고 상세로 이동 */}
            {selectedSimilar && (
                <BidDetailModal
                    isOpen={true}
                    onClose={() => setSelectedSimilar(null)}
                    bidNumber={selectedSimilar.bizNo}
                    bidNoticeOrder={selectedSimilar.bizOrd}
                />
            )}
        </>
    );
};

export default BidDetailModal;